import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';

const PartialRefundDialog = ({ open, onOpenChange, onConfirm, maxAmount, processing }) => {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue <= 0) {
      setError('Import ha de ser major que 0');
    } else if (numValue > maxAmount) {
      setError(`Import màxim: ${maxAmount.toFixed(2)}€`);
    } else {
      setError('');
    }
  };

  const handleConfirm = () => {
    const numValue = parseFloat(amount);
    if (!isNaN(numValue) && numValue > 0 && numValue <= maxAmount) {
      onConfirm(numValue);
      setAmount('');
      setError('');
    }
  };

  const isValid = amount && !error && parseFloat(amount) > 0 && parseFloat(amount) <= maxAmount;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
              <RefreshCw className="h-6 w-6 text-orange-600" />
            </div>
            <AlertDialogTitle className="text-2xl">
              Reemborsament Parcial
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-base leading-relaxed pt-4">
            <div className="space-y-4">
              <p className="text-foreground font-medium">
                Introdueix l'import que vols reemborsar:
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="refund-amount">Import a reemborsar (€)</Label>
                <Input
                  id="refund-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={maxAmount}
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder={`Màxim: ${maxAmount.toFixed(2)}€`}
                  className="text-lg"
                  autoFocus
                />
                {error && (
                  <p className="text-sm text-red-600">{error}</p>
                )}
              </div>

              <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Total comanda:</span>
                  <span className="text-lg font-bold text-foreground">{maxAmount.toFixed(2)}€</span>
                </div>
                {amount && isValid && (
                  <div className="flex justify-between items-center pt-2 border-t border-border">
                    <span className="text-muted-foreground">A reemborsar:</span>
                    <span className="text-xl font-bold text-orange-600">{parseFloat(amount).toFixed(2)}€</span>
                  </div>
                )}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                <p className="text-sm text-yellow-800 font-medium flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Aquesta acció no es pot desfer</span>
                </p>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-3 sm:gap-3">
          <AlertDialogCancel 
            disabled={processing}
            className="flex-1"
          >
            Cancel·lar
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={processing || !isValid}
            className="flex-1 bg-orange-600 hover:bg-orange-700"
          >
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Processant...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reemborsar
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default PartialRefundDialog;
